import omit from 'lodash/omit';
import orderBy from 'lodash/orderBy';
import map from 'lodash/map';
import get from 'lodash/get';
import omitDeep from 'omit-deep-lodash';

// An answer's mp3 url is `<host>/<bucket>/<prefix>/en/<voiceId>/<sha1(text)>.mp3`,
// where the prefix is per-environment configuration (dev writes
// `synthesized-speech-files/dev`, prod reads `synthetized-speech-files`) and the
// host has appeared in two forms over the years. Everything from `en/` onward is
// derived purely from case content, so keeping just that tail still catches a
// changed voice or changed wording while ignoring differences that only reflect
// where the file lives.
//
// Taken from the right because both parts to the left vary independently: the
// prefix is one segment in some environments and two in others.
const AUDIO_KEY_TAIL_SEGMENTS = 3;

const normalizeAudioUrl = (mp3) => {
  if (typeof mp3 !== 'string' || !mp3) return mp3;
  const segments = mp3.split(/[?#]/)[0].split('/').filter(Boolean);
  if (segments.length < AUDIO_KEY_TAIL_SEGMENTS) return mp3;
  return segments.slice(-AUDIO_KEY_TAIL_SEGMENTS).join('/');
};

const normalizeAnswerAudio = (answers) =>
  (answers || []).map((answer) =>
    answer && answer.audioClip
      ? {
          ...answer,
          audioClip: {
            ...answer.audioClip,
            mp3: normalizeAudioUrl(answer.audioClip.mp3),
          },
        }
      : answer
  );

const removeUncomparableCaseFields = (caseData) => ({
  ...omit(caseData, 'caseId', 'isFavorite', 'lastAccessed'),
  callees:
    caseData.callees &&
    omitDeep(caseData.callees, 'typeID', 'id').map((callee) => ({
      ...callee,
      answers: normalizeAnswerAudio(callee.answers),
    })),
  // npcs are otherwise spread through untouched, so their answers — audio urls
  // included — would be compared verbatim.
  npcs:
    caseData.npcs &&
    caseData.npcs.map((npc) => ({
      ...npc,
      answers: normalizeAnswerAudio(npc.answers),
    })),
  patients: caseData.patients.map((patient) => ({
    ...patient,
    answers: normalizeAnswerAudio(omitDeep(patient.answers, 'id')),
    commands: omitDeep(patient.commands, 'typeID'),
    questions: omitDeep(patient.questions, 'typeID'),
    initialPhysicalAssessments: map(
      // Assessment order might be different between envs
      orderBy(patient.initialPhysicalAssessments, 'location'),
      // Assessment index might move around between environments
      // But what we are interested in is that the label stays the same
      (initialPhysicalAssessment) => ({
        ...omit(initialPhysicalAssessment, 'assessmentIndex'),
        assessment: get(
          patient.physicalAssessments[
            initialPhysicalAssessment.assessmentIndex
          ],
          'label'
        ),
      })
    ),
    physicalAssessments: orderBy(patient.physicalAssessments, 'label'),
    patientLogic: omitDeep(patient.patientLogic, 'typeID', 'statementTargetId', '_databaseID'),
    initialMoulages: orderBy(patient.initialMoulages, 'location'),
    initialStructuralMoulages: orderBy(patient.initialStructuralMoulages, 'location'),
  })),
  medications:
    caseData.medications &&
    caseData.medications.map((med) =>
      med.isCustom ? omit(med, 'typeID', '_databaseID') : med
    ),
  grading: caseData.grading && {
    ...caseData.grading,
    calls: omitDeep(caseData.grading.calls, 'id', 'componentKey', 'subComponentKey'),
    commands: omitDeep(caseData.grading.commands, 'id', 'componentKey', 'subComponentKey'),
    questions: omitDeep(caseData.grading.questions, 'id', 'componentKey', 'subComponentKey'),
    medications: omitDeep(caseData.grading.medications, 'id'),
  }
});

export default removeUncomparableCaseFields;
